import { logSafeErrorWithoutCustomerDataWrapper } from '@jira/polaris__lib-logging/src/logSafeErrorWithoutCustomerDataWrapper.tsx';

import type { FieldKey } from '@jira/polaris__domain-field/src/FieldKey.types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import type { Action } from '@atlassian/react-sweet-state';
import type { Props, State } from './FieldsUpdate.types.tsx';

type IssueTypeId = string;

export const loadGlobalFieldsAssociations =
	(fieldKeys: FieldKey[]): Action<State, Props> =>
	async ({ getState, setState }, { globalFieldRemote }) => {
		const {
			meta: { connectionsLoadingFieldsAssociations },
			connectionsFieldsAssociations,
		} = getState();

		const fieldKeysToSend = fieldKeys.filter(
			(fieldKey) =>
				!connectionsFieldsAssociations[fieldKey] &&
				!connectionsLoadingFieldsAssociations.includes(fieldKey),
		);

		if (!fieldKeysToSend.length) {
			return;
		}

		setState({
			meta: {
				...getState().meta,
				connectionsLoadingFieldsAssociations: [
					...getState().meta.connectionsLoadingFieldsAssociations,
					...fieldKeysToSend,
				],
			},
		});

		try {
			const result = await globalFieldRemote.getProjectsForFields({
				fieldKeys: fieldKeysToSend,
			});

			const remappedFields = Object.entries(result).reduce<Record<FieldKey, IssueTypeId[]>>(
				(acc, [fieldKey, projects]) => {
					const issueIdsFromProjects = projects.reduce<string[]>((accInner, project) => {
						if (project.issueTypes) {
							return accInner.concat(project.issueTypes.map(({ id }) => String(id)));
						}
						return accInner;
					}, []);

					acc[fieldKey] = issueIdsFromProjects;

					return acc;
				},
				{},
			);

			setState({
				meta: {
					...getState().meta,
					connectionsLoadingFieldsAssociations:
						getState().meta.connectionsLoadingFieldsAssociations.filter((key) =>
							fieldKeysToSend.includes(key),
						),
				},
				connectionsFieldsAssociations: {
					...getState().connectionsFieldsAssociations,
					...remappedFields,
				},
			});
		} catch (error) {
			logSafeErrorWithoutCustomerDataWrapper(
				'polaris.error.fields.connections.loadGlobalFieldsAssociations',
				'Failed to load global fields associations for connection highlighted fields',
				error instanceof Error
					? error
					: new Error(
							'Error while loading global fields associations for connection highlighted fields',
						),
			);

			setState({
				meta: {
					...getState().meta,
					connectionsLoadingFieldsAssociations:
						getState().meta.connectionsLoadingFieldsAssociations.filter((key) =>
							fieldKeysToSend.includes(key),
						),
				},
			});
		}
	};
